import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApartmentTypeGroup } from 'types/accommodations';
import { ClientDataType, CommunityDataType, CompanyDataType, CompetitorSwotScoresType } from 'types/actors';
import { CommunityType } from 'types/communities';
import { CompanyBaseType } from 'types/company';
import { ContractType } from 'types/contracts';

export type DataType = {
  id: number;
  type: string;
  abv?: string;
  created_at: string;
  updated_at: string;
};

type ReviewCategoryDataType = {
  id: number;
  label: string;
};

export type PositionPermissionGroupDataType = {
  id: number;
  name: string;
};

export type PositionRightDataType = {
  id: number;
  name: string;
  permission_groups?: PositionPermissionGroupDataType[];
};

interface UniqueListType {
  loading: boolean;
  feePricingTypes: DataType[];
  apartmentTypes: DataType[];
  apartmentTypeGroups: ApartmentTypeGroup[];
  livingTypes: DataType[];
  contracts: ContractType[];
  reviewCategories: ReviewCategoryDataType[];
  positions: PositionRightDataType[];
}

interface GlobalType extends UniqueListType {
  reviewCategories: ReviewCategoryDataType[];
  contextCompany: CompanyBaseType | undefined;
  communities: CommunityType[];
  competitors: CommunityType[];
  companies: any[];
  communityMap: { [key: number]: CommunityType };
  careList: any[];
  views: any[];
  selectedClient: ClientDataType | undefined;
  selectedCompany: CompanyDataType | undefined;
  selectedCommunity: CommunityDataType | undefined;
  selectedParentCommunity: CommunityType | undefined;
}

// Define the initial state using that type
const initialState: GlobalType = {
  loading: true,
  feePricingTypes: [],
  apartmentTypes: [],
  apartmentTypeGroups: [],
  livingTypes: [],
  reviewCategories: [],
  positions: [],
  contextCompany: undefined,
  communities: [],
  competitors: [],
  communityMap: {},
  companies: [],
  careList: [],
  views: [],
  contracts: [],
  selectedClient: undefined,
  selectedCompany: undefined,
  selectedCommunity: undefined,
  selectedParentCommunity: undefined,
};

export const globalSlice = createSlice({
  name: 'global',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handleSetOptionTypes: (state, action: PayloadAction<UniqueListType>) => {
      state.feePricingTypes = action.payload.feePricingTypes ?? [];
      state.apartmentTypes = action.payload.apartmentTypes ?? [];
      state.apartmentTypeGroups = action.payload.apartmentTypeGroups ?? [];
      state.livingTypes = action.payload.livingTypes ?? [];
      state.contracts = action.payload.contracts ?? [];
      state.reviewCategories = action.payload.reviewCategories ?? [];
      state.positions = action.payload.positions ?? [];
    },
    handleSetLoader: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    handleStoreCommunityMap: (state, action: PayloadAction<any[]>) => {
      state.communityMap = (action.payload as any) ?? {};
    },
    handleSetContextCompany: (state, action: PayloadAction<CompanyBaseType | undefined>) => {
      state.contextCompany = action.payload;
    },
    handleStoreUserCommunities: (state, action: PayloadAction<any[]>) => {
      state.communities = action.payload ?? [];
    },
    handleStoreUserCommunitiesCompetitors: (state, action: PayloadAction<any[]>) => {
      state.competitors = action.payload ?? [];
    },
    handleStoreCompanies: (state, action: PayloadAction<any[]>) => {
      state.companies = action.payload ?? [];
    },
    handleStoreViews: (state, action: PayloadAction<any[]>) => {
      state.views = action.payload ?? [];
    },
    handleSetSelectedClient: (state, action: PayloadAction<ClientDataType | undefined>) => {
      state.selectedClient = action.payload;
    },
    handleSetSelectedCompany: (state, action: PayloadAction<CompanyDataType | undefined>) => {
      state.selectedCompany = action.payload;
    },
    handleSetSelectedCommunity: (state, action: PayloadAction<CommunityDataType | undefined>) => {
      state.selectedCommunity = action.payload;
    },
    handleSetSelectedParentCommunity: (state, action: PayloadAction<CommunityType | undefined>) => {
      state.selectedParentCommunity = action.payload;
    },
    handleReset: (state) => {
      state.loading = true;
      state.contextCompany = undefined;
      state.companies = [];
      state.views = [];
      state.selectedClient = undefined;
      state.selectedCompany = undefined;
      state.selectedCommunity = undefined;
      state.selectedParentCommunity = undefined;
      state.communities = [];
      state.competitors = [];
      state.communityMap = {};
    },
    handleSetCompetitorSwotScores: (state, action: PayloadAction<CompetitorSwotScoresType>) => {
      const competitor = state.competitors.find((el) => el?.id === action.payload.competitorId);
      const scores: { [key: string]: string | number } = action.payload.scores;

      // TODO: Uncomment and implement the logic to set SWOT scores for the competitor
      // if (competitor && scores) {
      //   Object.keys(scores)
      //     // filter only the swot score properties, not the rest of the Strengthfinder model
      //     // properties (community_name, community_id etc...)
      //     .filter((propertyName) => Object.keys(DEFAULT_SWOT_SCORES).indexOf(propertyName) !== -1)
      //     .forEach((propertyName: string) => {
      //       competitor[propertyName] = scores[propertyName];
      //     });
      // }
    },
  },
});

export const {
  handleSetOptionTypes,
  handleStoreCompanies,
  handleSetContextCompany,
  handleStoreUserCommunities,
  handleStoreCommunityMap,
  handleStoreUserCommunitiesCompetitors,
  handleStoreViews,
  handleSetLoader,
  handleSetSelectedClient,
  handleSetSelectedCompany,
  handleSetSelectedCommunity,
  handleSetSelectedParentCommunity,
  handleReset,
  handleSetCompetitorSwotScores,
} = globalSlice.actions;

export default globalSlice.reducer;
